import { Injectable, OnDestroy } from '@angular/core'
import { UntypedFormArray, UntypedFormControl, UntypedFormGroup } from '@angular/forms'
import { BehaviorSubject, Subscription } from 'rxjs'
import {
  EbDependsOnConfig,
  EbFormConfig,
  EbFormDependencyResultMap,
  EbFormElementConfig,
  EbFormElementsConfig,
  EbFormGroupConfig,
  EbValidatorFunctionConfig
} from '../interfaces/eb-form.interface'

@Injectable({ providedIn: 'root' })
export class EbFormDependenciesService implements OnDestroy {
  private readonly subscriptions = new Subscription()

  private static getDefaultState(dependsOn: EbDependsOnConfig) {
    if (dependsOn.hide) {
      return 'hide'
    } else if (dependsOn.disable) {
      return 'disable'
    }
    return null
  }

  private static appendToResultMap(
    path: string,
    defaultState: string,
    bool: boolean,
    resultMap: EbFormDependencyResultMap
  ) {
    const subject = new BehaviorSubject(bool)
    if (!resultMap[path]) {
      resultMap[path] = {}
    }
    if (!resultMap[path][defaultState]) {
      resultMap[path][defaultState] = []
    }
    resultMap[path][defaultState].push(subject)
    return subject
  }

  private static getCondition(config: EbValidatorFunctionConfig) {
    const arg0value = config.arguments?.[0]?.value
    switch (config.name) {
      case 'checkValue':
        return (ctrl: UntypedFormControl | UntypedFormArray) => {
          const value = ctrl.value
          return value instanceof Array ? value.includes(arg0value) : value === arg0value
        }
      case 'checkValueNot':
        return (ctrl: UntypedFormControl | UntypedFormArray) => {
          const value = ctrl.value
          return !(value instanceof Array ? value.includes(arg0value) : value === arg0value)
        }
      default:
        throw new Error(`Unknown condition '${config.name}'`)
    }
  }

  generateDependencyMap(
    formConfig: EbFormConfig,
    formGroup: UntypedFormGroup
  ): EbFormDependencyResultMap {
    const resultMap = {}
    formConfig.steps.forEach(step => {
      this.processEbFormElementsConfig('', step.elements, formGroup, resultMap)
    })
    return resultMap
  }

  private processEbFormElementsConfig(
    path: string,
    elements: EbFormElementsConfig,
    formGroup: UntypedFormGroup,
    resultMap: EbFormDependencyResultMap
  ): void {
    elements.forEach(element =>
      this.processEbFormElementConfig(
        (path ? path + '.' : '') + element.name,
        element,
        formGroup,
        resultMap
      )
    )
  }

  private processEbFormElementConfig(
    path: string,
    element: EbFormElementConfig,
    formGroup: UntypedFormGroup,
    resultMap: EbFormDependencyResultMap
  ): void {
    if (element.dependsOn) {
      this.handleDependsOn(path, element.dependsOn, formGroup, resultMap)
    }
    if (element.type === 'group' && (element as EbFormGroupConfig).elements?.length) {
      this.processEbFormElementsConfig(
        path,
        (element as EbFormGroupConfig).elements,
        formGroup,
        resultMap
      )
    }
  }

  /**
   * Currently only single Controls with single condition 'checkValue' are supported
   * @param path
   * @param dependsOn
   * @param formGroup
   * @param resultMap
   */
  private handleDependsOn(
    path: string,
    dependsOn: EbDependsOnConfig,
    formGroup: UntypedFormGroup,
    resultMap: EbFormDependencyResultMap
  ) {
    const defaultState = EbFormDependenciesService.getDefaultState(dependsOn)
    if (!defaultState) {
      return
    }
    dependsOn.controls?.forEach(controlConfig => {
      const control = formGroup.get(controlConfig.controlPath) as
        | UntypedFormControl
        | UntypedFormArray
      if (!control) {
        throw new Error(
          `Error in DependsOn-configuration: Unkown control '${controlConfig.controlPath}'`
        )
      }
      controlConfig.conditions.forEach(conditionConfig => {
        this.processControlCondition(path, defaultState, control, conditionConfig, resultMap)
      })
    })
  }

  private processControlCondition(
    path: string,
    defaultState: string,
    control: UntypedFormControl | UntypedFormArray,
    conditionConfig,
    resultMap: EbFormDependencyResultMap
  ) {
    const conditionFunction = EbFormDependenciesService.getCondition(conditionConfig)
    const subject = EbFormDependenciesService.appendToResultMap(
      path,
      defaultState,
      conditionFunction(control),
      resultMap
    )
    this.subscriptions.add(
      control.valueChanges.subscribe(() => {
        subject.next(conditionFunction(control))
      })
    )
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe()
  }
}
